import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { 
  Zap, Search, Package, Calculator, TrendingUp, 
  ChevronRight, X, Sparkles, Calendar 
} from 'lucide-react';

const ONBOARDING_STORAGE_KEY = 'flipvalue_onboarding_completed';

const onboardingSteps = [
  {
    icon: Search,
    title: 'Search & Value',
    description: 'Search any item by name to instantly get market valuations from real eBay listings.',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  {
    icon: Package,
    title: 'Track Inventory',
    description: 'Add items to your inventory to track their value over time. Organize with custom categories and set price alerts.',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10'
  },
  {
    icon: Calculator,
    title: 'Calculate Profits',
    description: 'Use the profit calculator to see your actual earnings after platform fees on eBay, Mercari, Poshmark, and more.',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10'
  },
  {
    icon: Calendar,
    title: 'Release Schedule',
    description: 'Track upcoming releases and set pre-order reminders so you never miss a drop.',
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10'
  }
];

export default function OnboardingModal({ forceShow = false, onComplete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (forceShow) {
      setIsOpen(true);
      setCurrentStep(0);
      return;
    }
    
    // Check if user has completed onboarding
    const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!completed) {
      // Small delay to let the page load first
      const timer = setTimeout(() => setIsOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setIsOpen(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setIsOpen(false);
    onComplete?.();
  };

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const currentStepData = onboardingSteps[currentStep];
  const IconComponent = currentStepData.icon;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) handleSkip();
    }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 pt-10">
          {/* Step indicator */}
          <div className="flex justify-center gap-2 mb-6">
            {onboardingSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? 'w-6 bg-primary' 
                    : index < currentStep 
                      ? 'bg-primary/50' 
                      : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl ${currentStepData.bgColor} flex items-center justify-center mx-auto mb-4`}>
            <IconComponent className={`w-8 h-8 ${currentStepData.color}`} />
          </div>

          {/* Title & Description */}
          <h2 className="text-xl font-bold text-center mb-2">
            {currentStepData.title}
          </h2>
          <p className="text-muted-foreground text-center text-sm mb-6">
            {currentStepData.description}
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={handleSkip}
            >
              Skip
            </Button>
            <Button
              className="flex-1 gap-2"
              onClick={handleNext}
            >
              {currentStep === onboardingSteps.length - 1 ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  Get Started
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Bottom illustration */}
        <div className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500" />
      </DialogContent>
    </Dialog>
  );
}

// Hook to reset onboarding (for testing or settings)
export function useOnboarding() {
  const resetOnboarding = () => {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  };

  const hasCompletedOnboarding = () => {
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
  };

  return { resetOnboarding, hasCompletedOnboarding };
}
